import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useScrollToTopOnMount } from '../../hooks/useScrollToTopOnMount';
import useSearch from '../../hooks/useSearch';
import { searchKeyMinLength } from '../../utils/constants';
import { mock } from '../../utils/mock';
import { PAGE_SIZE, searchOptions } from './Search.constants';
import { TResult, TSearchData } from './Search.types';
import useDeviceTypeDetector from '../../hooks/useDeviceTypeDetector/useDeviceTypeDetector';

import Button from '../../ui/Button/Button';
import Text from '../../ui/Text/Text';
import {
  getHighlightedMatchValue,
  getSearchItemBreadCrumbs,
  getSubtitleText,
} from './Search.helpers';

import Icon from '../../ui/Icon/Icon';
import Pagination from '../../components/Pagination/Pagination';

import './search.scss';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { AccessibilityModeEnum } from '../../utils/enums';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { redirectToSearchPage } = useSearch();
  const { isMobile } = useDeviceTypeDetector();
  const {
    settings: { mode },
  } = useAccessibility();

  const searchResultsHeaderRef = useRef<null | HTMLDivElement>(null);

  useScrollToTopOnMount();

  const navigate = useNavigate();
  const query = searchParams.get('key');
  const page = Number(searchParams.get('page') || 1);

  const [currentPage, setCurrentPage] = useState(Number(page) - 1);
  const [searchKey, setSearchKey] = React.useState(query || '');

  const fuse = useMemo(() => new Fuse(mock as TSearchData, searchOptions), []);
  const results = useMemo(() => (query ? fuse.search(query) : []), [query]);
  const isKeyValid = useMemo(() => searchKey.trim().length >= searchKeyMinLength, [searchKey]);

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        redirectToSearchPage(e.currentTarget.value);
      }
    },
    [redirectToSearchPage]
  );

  const handleClick = useCallback(() => {
    redirectToSearchPage(searchKey);
  }, [redirectToSearchPage, searchKey]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchKey(e.currentTarget.value);
    },
    [setSearchKey]
  );

  const handleSearchBtnClick = useCallback(() => {
    if (!isKeyValid) return;
    redirectToSearchPage(searchKey);
  }, [redirectToSearchPage, isKeyValid]);

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      const params = new URLSearchParams(searchParams);
      const newPage = Number(pageNumber) || 1;

      params.set('page', String(newPage));

      setCurrentPage(newPage - 1);
      setSearchParams(params, { replace: false });
      searchResultsHeaderRef.current?.scrollIntoView();
    },
    [searchKey]
  );

  useEffect(() => {
    if (!query) return;

    setSearchKey(query);
  }, [query]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formattedData = useMemo(() => {
    const formattedResults: TResult[] = [];

    results.forEach((r) => r.matches?.forEach((m) => formattedResults.push({ ...m, ...r.item })));

    return {
      data: formattedResults,
      totalCount: formattedResults?.length || 0,
    };
  }, [results]);

  useEffect(() => {
    if(formattedData.totalCount <= currentPage * PAGE_SIZE) {
      handlePageChange(1);
    };
  }, [formattedData, currentPage]);

  const slicedData = useMemo(() => {
    const remainder = Math.ceil(formattedData.totalCount / PAGE_SIZE) - 1;
    const fixedPage = Math.min(remainder, currentPage);

    return remainder > 0
      ? formattedData.data.slice(fixedPage * PAGE_SIZE, fixedPage * PAGE_SIZE + PAGE_SIZE)
      : formattedData.data;
  }, [formattedData, currentPage]);

  return (
    <main className='page search-page'>
      <div className='container search-holder'>
        <section className='search-input-section'>
          <Text variant='h2' fontWeight={600} textTransform='upperCase'>
            Поиск
          </Text>
          <div className='search-controls'>
            <div className='input-row'>
              <input
                value={searchKey}
                placeholder='Поиск по сайту'
                className='search-input'
                onKeyDown={handleKeydown}
                onChange={handleChange}
              />
              <button
                type='button'
                aria-label='Посмотреть результаты'
                onClick={handleSearchBtnClick}
              >
                <Icon
                  name={
                    isKeyValid && mode !== AccessibilityModeEnum.Enhanced ? 'loopActive' : 'loop'
                  }
                  size={24}
                  className='search-icon'
                />
              </button>
            </div>
            {!isMobile && (
              <Button type='button' variant='secondary' onClick={handleClick}>
                <Text variant='body1' fontWeight={600}>
                  Подробнее
                </Text>
              </Button>
            )}
          </div>
        </section>
        {query && (
          <section className='search-header' ref={searchResultsHeaderRef}>
            <Text variant='h3' fontWeight={600} className='header-title'>
              Результаты Поиска{' '}
            </Text>
            <Text variant='subtitle1' className='header-sub-title'>
              {getSubtitleText(formattedData.totalCount, currentPage, query || '')}
            </Text>
          </section>
        )}
        <section className='search-results'>
          <div>
            {slicedData?.map((item, i) => {
              const hashUrl = `${item.path}#${item.id}`;
              const breadCrumbs = getSearchItemBreadCrumbs(item, query);

              // TODO: Move to another component
              // eslint-disable-next-line
              const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                e.stopPropagation();

                navigate(hashUrl);
              };

              return (
                <a key={`${i}`} className='search-result-item' href={hashUrl}>
                  {!!breadCrumbs.length && (
                    <div className='breadcrumbs'>
                      {breadCrumbs.map(({ text, href }, index) => {
                        return (
                          <a key={index} href={href} onClick={handleClick}>
                            <Text
                              variant='body1'
                              className='breadcrumb-text'
                              textTransform='capitalize'
                              fontWeight={500}
                            >
                              {text}
                            </Text>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {item.value && (
                    <div
                      className='match'
                      // eslint-disable-next-line
                      dangerouslySetInnerHTML={{
                        __html: getHighlightedMatchValue(item.value, item.indices),
                      }}
                    />
                  )}
                </a>
              );
            })}
          </div>
          {formattedData.totalCount > PAGE_SIZE && (
            <Pagination
              total={formattedData.totalCount}
              current={currentPage + 1}
              pageSize={PAGE_SIZE}
              onChange={handlePageChange}
            />
          )}
        </section>
      </div>
    </main>
  );
};

export default Search;
